import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import axios from 'axios';
import { AuthContext } from '../App';
import { useSubscription } from '../context/SubscriptionContext';
import { BookOpen, Play, Check, X, Star, ArrowLeft, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import UpgradeModal from '../components/UpgradeModal';
import './Games.css';
import './Quiz.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Quizzes() {
  const { t } = useTranslation();
  const { user } = React.useContext(AuthContext);
  const { trackPlay, playedGamesCount, maxPlayedGames, canPlayMore, isPremium } = useSubscription();
  const [quizzes, setQuizzes] = useState([]);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  useEffect(() => {
    loadQuizzes();
  }, []);
  
  const loadQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/quizzes`, { withCredentials: true });
      setQuizzes(response.data);
    } catch (error) {
      console.error('Failed to load quizzes:', error);
    }
  };
  
  const startQuiz = async (quiz) => {
    // Check if user can play before tracking
    if (!isPremium && !canPlayMore) {
      setShowUpgradeModal(true);
      return;
    }
    
    // Track play when user starts a quiz
    const result = await trackPlay(quiz.id, 'quiz');
    if (!result.success && result.limitReached) {
      setShowUpgradeModal(true);
      return;
    }
    
    setSelectedQuiz(quiz);
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setQuizComplete(false);
  };
  
  const handleAnswerSelect = (answer) => {
    if (showFeedback) return;
    setSelectedAnswer(answer);
  };
  
  const submitAnswer = () => {
    const question = selectedQuiz.questions[currentQuestion];
    const isCorrect = selectedAnswer === question.correct_answer;
    
    if (isCorrect) {
      setScore(score + 1);
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { y: 0.7 }
      });
      playSound('correct');
      if (window.showMascotMessage) {
        window.showMascotMessage(t('correct'));
      }
    } else {
      playSound('wrong');
      if (window.showMascotMessage) {
        window.showMascotMessage(t('tryAgain'));
      }
    }
    
    setShowFeedback(true);
    
    setTimeout(() => {
      if (currentQuestion < selectedQuiz.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer(null);
        setShowFeedback(false);
      } else {
        setQuizComplete(true);
        saveProgress();
      }
    }, 2000);
  };
  
  const playSound = (type) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'correct') {
      oscillator.frequency.value = 523.25;
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } else {
      oscillator.frequency.value = 200;
      oscillator.type = 'sawtooth';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    }
  };
  
  const saveProgress = async () => {
    try {
      const stars = Math.ceil((score / selectedQuiz.questions.length) * 5);
      await axios.post(`${API}/progress`, {
        activity_id: selectedQuiz.id,
        activity_type: 'quiz',
        score: score * 10,
        stars: stars
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };
  
  const backToQuizzes = () => {
    setSelectedQuiz(null);
    setQuizComplete(false);
  };
  
  const deleteQuiz = async (quizId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this quiz?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/quizzes/${quizId}`, { withCredentials: true });
      toast.success('Quiz deleted successfully!');
      loadQuizzes();
    } catch (error) {
      console.error('Failed to delete quiz:', error);
      toast.error('Failed to delete quiz');
    }
  };
  
  if (selectedQuiz && !quizComplete) {
    const question = selectedQuiz.questions[currentQuestion];
    const isCorrect = selectedAnswer === question.correct_answer;
    
    return (
      <div className="quiz-player-page" data-testid="quiz-player">
        <div className="quiz-container">
          <button onClick={backToQuizzes} className="back-button" data-testid="back-button">
            <ArrowLeft size={20} />
            {t('home')}
          </button>
          
          <div className="quiz-header">
            <h2>{selectedQuiz.title}</h2>
            <div className="quiz-progress">
              <span>Question {currentQuestion + 1} / {selectedQuiz.questions.length}</span>
              <div className="score-display">
                <Star size={20} color="#FFE66D" />
                <span>{score} / {selectedQuiz.questions.length}</span>
              </div>
            </div>
          </div>
          
          <motion.div
            className="question-card"
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h3 className="question-text" data-testid="quiz-question">{question.question}</h3>
            
            <div className="options-grid">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(option)}
                  className={`option-button ${selectedAnswer === option ? 'selected' : ''} ${
                    showFeedback && option === question.correct_answer ? 'correct' : ''
                  } ${
                    showFeedback && selectedAnswer === option && !isCorrect ? 'wrong' : ''
                  }`}
                  disabled={showFeedback}
                  data-testid={`option-${index}`}
                >
                  {option}
                </button>
              ))}
            </div>
            
            {!showFeedback && selectedAnswer && (
              <button 
                onClick={submitAnswer}
                className="btn btn-primary submit-btn"
                data-testid="submit-quiz-answer"
              >
                {t('submit')}
              </button>
            )}
            
            <AnimatePresence>
              {showFeedback && (
                <motion.div
                  className={`feedback ${isCorrect ? 'correct' : 'wrong'}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  data-testid="quiz-feedback"
                >
                  {isCorrect ? (
                    <>
                      <Check size={32} />
                      <span>{t('correct')}</span>
                    </>
                  ) : (
                    <>
                      <X size={32} />
                      <span>Correct answer: {question.correct_answer}</span>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    );
  }
  
  if (quizComplete) {
    const percentage = Math.round((score / selectedQuiz.questions.length) * 100);
    const stars = Math.ceil((score / selectedQuiz.questions.length) * 5);
    
    return (
      <div className="quiz-player-page" data-testid="quiz-complete">
        <div className="quiz-container">
          <motion.div
            className="completion-card"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Star className="star-icon" size={80} color="#FFE66D" />
            <h2>{t('greatJob')}</h2>
            <p className="completion-score">
              You scored {score} out of {selectedQuiz.questions.length}
            </p>
            <p className="completion-percentage">{percentage}%</p>
            <div className="stars-display">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={32}
                  color={i < stars ? '#FFE66D' : '#E0E0E0'}
                  fill={i < stars ? '#FFE66D' : 'none'}
                />
              ))}
            </div>
            <button 
              onClick={backToQuizzes}
              className="btn btn-primary"
              data-testid="back-to-quizzes"
            >
              Back to Quizzes
            </button>
          </motion.div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="game-page" data-testid="quizzes-page">
      <div className="game-container">
        <motion.div
          className="page-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <BookOpen size={48} color="#FF6B9D" />
          <h1>{t('quizzes')}</h1>
          <Link to="/quiz-builder" className="create-new-btn">
            <Plus size={20} />
            Create New Quiz
          </Link>
        </motion.div>
        
        {quizzes.length === 0 ? (
          <div className="empty-state">
            <p>No quizzes yet. Create your first quiz!</p>
            <Link to="/quiz-builder" className="btn btn-primary">
              <Plus size={18} />
              Create Quiz
            </Link>
          </div>
        ) : (
          <div className="items-grid">
            {quizzes.map((quiz, index) => (
              <motion.div
                key={quiz.id}
                className="item-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                data-testid={`quiz-card-${index}`}
              >
                <div className="item-header">
                  <BookOpen size={32} color="#FF6B9D" />
                  <h3>{quiz.title}</h3>
                  {quiz.created_by === user?.id && (
                    <button
                      onClick={(e) => deleteQuiz(quiz.id, e)}
                      className="delete-quiz-btn"
                      data-testid={`delete-quiz-${index}`}
                      title="Delete Quiz"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
                <p className="item-meta">
                  <strong>{quiz.questions.length}</strong> questions
                </p>
                <p className="item-difficulty">
                  {t('difficulty')}: {t(quiz.difficulty)}
                </p>
                <button 
                  onClick={() => startQuiz(quiz)}
                  className={`btn btn-primary btn-play ${!isPremium && !canPlayMore ? 'disabled' : ''}`}
                  disabled={!isPremium && !canPlayMore}
                  data-testid={`play-quiz-${index}`}
                >
                  <Play size={20} />
                  {t('play')}
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Upgrade Modal for play limit */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="play"
        currentCount={playedGamesCount}
        maxCount={maxPlayedGames || 3}
      />
    </div>
  );
}

export default Quizzes;
